import {Injectable} from '@angular/core';
import {Observable, of, switchMap} from 'rxjs';

import {LocalStorageService} from './local-storage.service';
import {PathEnum} from '../shared/enums/path-enum';
import {deveSaltareProfiloCittadino} from '../shared/utils/ruoli-token.util';

/**
 * Calcola la rotta corretta post-bootstrap in base allo stato dell'onboarding:
 *   operatore di backoffice   → area operatore (nessun onboarding cittadino)
 *   userProfile == null       → /onboarding/completa-profilo
 *   slugComune == null        → /onboarding/scegli-comune
 *   altrimenti                → /pages/home
 *
 * Nessuno step di accettazione documenti: i documenti del Comune si accettano in una modale
 * (`DocumentiComuneGateService`), che scatta all'apertura dell'app, dopo il login e alla scelta
 * del Comune, senza passare da una rotta.
 *
 * Riusato dai 3 punti di session bootstrap: AppInitService, auth-callback, login passkey.
 */
@Injectable({providedIn: 'root'})
export class OnboardingRouterService {
  constructor(private readonly localStorageService: LocalStorageService) {}

  computeNextRoute(): Observable<string> {
    return this.localStorageService.getSlugComune$().pipe(
      switchMap((slug) => this.resolveRoute$(slug))
    );
  }

  /**
   * Gli step leggono solo lo storage locale: nessuna chiamata di rete.
   */
  private resolveRoute$(slug: string | null | undefined): Observable<string> {
    // Operatore di backoffice: va valutato PRIMA di `userProfile == null`, altrimenti il ramo non
    // sarebbe mai raggiunto — per lui il profilo cittadino è null per costruzione, perché
    // `UserSessionService` non lo carica affatto (il matcher BE è CITTADINO-only).
    //
    // Il discrimine è il RUOLO NEL TOKEN, non l'assenza del profilo: `userProfile == null` è vero
    // anche per un cittadino appena registrato, e confondere i due casi manderebbe l'operatore
    // sull'onboarding cittadino (dati che non gli competono) oppure il cittadino nuovo nell'area
    // operatore. Unico lettore dei ruoli: `ruoli-token.util.ts`.
    const roles = this.localStorageService.decodeClaims()?.roles ?? [];
    if (deveSaltareProfiloCittadino(roles)) {
      return of(PathEnum.OPERATORE_HOME);
    }

    const userProfile = this.localStorageService.getUserProfile();
    if (userProfile == null) {
      return of(PathEnum.ONBOARDING_PROFILO);
    }
    if (!slug) {
      return of(PathEnum.ONBOARDING_COMUNE);
    }
    return of(PathEnum.PAGES + PathEnum.HOME);
  }
}
